import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  History,
  Loader2,
  RotateCcw,
  CheckCircle2,
  Building,
  Users,
  RefreshCw,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const formatTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const MergeHistory = () => {
  usePageTitle('Merge History');
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/merge/history?days=30`);
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load merge history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const performUndo = async () => {
    if (!confirmTarget) return;
    setUndoing(true);
    try {
      const res = await axios.post(`${API_URL}/merge/undo/${confirmTarget.id}`);
      const r = res.data.restored || {};
      toast.success(
        `Undone. Restored ${r.records_restored} record${r.records_restored === 1 ? '' : 's'} and reverted ${r.references_reverted} reference${r.references_reverted === 1 ? '' : 's'}.`
      );
      setConfirmTarget(null);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo failed');
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="merge-history-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin-settings')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Merge History
          </h1>
          <p className="text-slate-500 mt-1">
            Last 30 days of merges. Click <strong>Undo</strong> to restore a soft-deleted record and revert all references.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchHistory} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-slate-500">
            <History className="w-10 h-10 mx-auto mb-3 text-slate-400" />
            <p className="font-medium text-slate-900">No merges in the last 30 days</p>
            <p className="text-sm mt-1">Once you merge duplicates, they'll appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((m) => {
            const isCompany = m.action === 'merge_companies';
            const losers = m.details?.losers || [];
            const undone = !!m.undone;
            const hasDetailedRefs = losers.length > 0;
            const Icon = isCompany ? Building : Users;
            const iconColor = isCompany ? 'text-blue-600' : 'text-emerald-600';

            return (
              <Card key={m.id} className={undone ? 'opacity-60' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${iconColor}`} />
                      <div>
                        <CardTitle className="text-base">
                          {isCompany ? 'Merged companies into' : 'Merged students into'}{' '}
                          <span className="text-slate-900">
                            {m.details?.winner_name || m.details?.winner_id}
                          </span>
                        </CardTitle>
                        <CardDescription className="mt-1 text-xs">
                          By {m.user_email} · {formatTime(m.timestamp)}
                          {undone && (
                            <span className="ml-2 text-amber-700">
                              · Undone by {m.undone_by} on {formatTime(m.undone_at)}
                            </span>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {undone ? (
                        <span className="text-xs flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Undone
                        </span>
                      ) : hasDetailedRefs ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => setConfirmTarget(m)}
                          data-testid={`undo-${m.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1.5" /> Undo merge
                        </Button>
                      ) : (
                        <span
                          className="text-xs text-slate-500"
                          title="This merge happened before undo support; cannot auto-undo."
                        >
                          (legacy — undo not available)
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                {losers.length > 0 && (
                  <CardContent className="pt-0">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                      {losers.length} record{losers.length === 1 ? '' : 's'} merged in
                    </div>
                    <ul className="space-y-1 text-sm">
                      {losers.map((l) => {
                        const refCount = Object.values(l.refs || {}).reduce(
                          (n, arr) => n + (Array.isArray(arr) ? arr.length : 0),
                          0
                        );
                        return (
                          <li
                            key={l.id}
                            className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-50"
                          >
                            <span className="text-slate-700">{l.name || l.id}</span>
                            <span className="text-xs text-slate-500">
                              {refCount} reference{refCount === 1 ? '' : 's'} re-pointed
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmTarget} onOpenChange={(open) => !open && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this merge?</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2 text-sm">
                <div>
                  This will restore the merged record(s) and revert all references back to them.
                </div>
                {confirmTarget?.details?.losers && (
                  <div>
                    <strong>Records to restore:</strong>
                    <ul className="list-disc ml-5 mt-1">
                      {confirmTarget.details.losers.map((l) => (
                        <li key={l.id}>{l.name || l.id}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="text-xs text-slate-500 pt-2">
                  After undoing, you can re-merge differently or leave the records separate.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performUndo}
              disabled={undoing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {undoing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              {undoing ? 'Undoing...' : 'Yes, undo merge'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MergeHistory;
